import { useCallback, useEffect, useMemo, useState } from "react"
import { supabase } from "../supabaseClient"
import Dialog from "./Dialog"
import {
  Badge,
  Button,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "./ui"
import "./GA4OvernightAuditDialog.css"

const invokeNightly = async (body) => {
  const { data, error } = await supabase.functions.invoke("ga4-nightly", { body })
  if (!error) return data

  let message = error.message || "The overnight audit request failed."
  try {
    const payload = await error.context?.json?.()
    if (typeof payload?.error === "string") message = payload.error
  } catch {
    // The generic function error is still useful when no JSON response is available.
  }
  throw new Error(message)
}

const getRun = (item) => {
  const relation = item?.ga4_nightly_runs
  return Array.isArray(relation) ? relation[0] : relation
}

const formatTimestamp = (value) => {
  if (!value) return "Not run"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "Not run"
  return date.toLocaleString()
}

const statusVariant = (status) => {
  if (status === "succeeded") return "accent"
  if (status === "failed" || status === "partial") return "outline"
  return "subtle"
}

const statusLabel = (status) => {
  if (!status) return "Not run"
  return status.replaceAll("_", " ")
}

export default function GA4OvernightAuditDialog({
  isOpen,
  onClose,
  organizationId,
}) {
  const [data, setData] = useState(null)
  const [selectedIds, setSelectedIds] = useState(() => new Set())
  const [initialIds, setInitialIds] = useState(() => new Set())
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [retryingId, setRetryingId] = useState(null)
  const [error, setError] = useState("")

  const loadSchedule = useCallback(async () => {
    if (!organizationId) return
    setLoading(true)
    setError("")
    try {
      const response = await invokeNightly({
        action: "get-schedule",
        organizationId,
      })
      const ids = new Set(
        (response?.schedules || []).map((schedule) => schedule.ga4_property_id),
      )
      setData(response)
      setSelectedIds(ids)
      setInitialIds(new Set(ids))
    } catch (loadError) {
      setError(loadError.message)
    } finally {
      setLoading(false)
    }
  }, [organizationId])

  useEffect(() => {
    if (isOpen) loadSchedule()
  }, [isOpen, loadSchedule])

  const scheduleByProperty = useMemo(
    () => new Map((data?.schedules || []).map((row) => [row.ga4_property_id, row])),
    [data?.schedules],
  )
  const latestByProperty = useMemo(() => {
    const map = new Map()
    for (const item of data?.history || []) {
      const key = item.ga4_property_id || item.property_record_id_snapshot
      if (key && !map.has(key)) map.set(key, item)
    }
    return map
  }, [data?.history])
  const failedLatestItems = useMemo(
    () => [...latestByProperty.values()].filter(
      (item) => item.status === "failed" || item.status === "partial",
    ),
    [latestByProperty],
  )
  const hasChanges = useMemo(() => {
    if (selectedIds.size !== initialIds.size) return true
    return [...selectedIds].some((id) => !initialIds.has(id))
  }, [initialIds, selectedIds])

  const toggleProperty = (propertyId) => {
    setSelectedIds((current) => {
      const next = new Set(current)
      if (next.has(propertyId)) next.delete(propertyId)
      else next.add(propertyId)
      return next
    })
  }

  const saveSchedule = async () => {
    setSaving(true)
    setError("")
    try {
      await invokeNightly({
        action: "replace-schedule",
        organizationId,
        propertyIds: [...selectedIds],
      })
      await loadSchedule()
    } catch (saveError) {
      setError(saveError.message)
    } finally {
      setSaving(false)
    }
  }

  const retryItem = async (item) => {
    setRetryingId(item.id)
    setError("")
    try {
      await invokeNightly({ action: "retry-item", itemId: item.id })
      await loadSchedule()
    } catch (retryError) {
      setError(retryError.message)
    } finally {
      setRetryingId(null)
    }
  }

  const retryAll = async () => {
    let retryFailure = ""
    for (const item of failedLatestItems) {
      setRetryingId(item.id)
      try {
        await invokeNightly({ action: "retry-item", itemId: item.id })
      } catch (retryError) {
        retryFailure = retryError.message
        break
      }
    }
    setRetryingId(null)
    await loadSchedule()
    if (retryFailure) setError(retryFailure)
  }

  return (
    <Dialog
      isOpen={isOpen}
      onClose={saving || retryingId ? undefined : onClose}
      title="Manage Overnight Audits"
      className="ga4-overnight-dialog"
    >
      <p className="ga4-overnight-dialog__intro">
        Selected GA4 properties run nightly at 2:15 AM Eastern. Audit coverage follows
        this organization&apos;s enabled GA4 products.
      </p>

      {loading && !data ? <p>Loading overnight audit settings…</p> : null}
      {error ? <p className="dialog__error" role="alert">{error}</p> : null}

      {data ? (
        <>
          <div className="ga4-overnight-dialog__summary">
            <Badge variant={data.eligible ? "accent" : "outline"}>
              {data.eligible ? "Daily scheduling available" : "Manual-only plan"}
            </Badge>
            <span>
              Licensed coverage: {[
                data.licensed?.ga4Audit && "GA4 configuration",
                data.licensed?.ascEventUsage && "ASC event usage",
              ].filter(Boolean).join(" + ") || "none"}
            </span>
          </div>
          {!data.canManage ? (
            <p className="ga4-overnight-dialog__notice">
              Only organization admins can change this list or retry failed audits.
            </p>
          ) : null}
          {!data.eligible ? (
            <p className="ga4-overnight-dialog__notice">
              Existing selections may be removed. New properties require a Daily-capable plan.
            </p>
          ) : null}

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Overnight</TableHeaderCell>
                  <TableHeaderCell>Property</TableHeaderCell>
                  <TableHeaderCell>Linked by</TableHeaderCell>
                  <TableHeaderCell>Schedule state</TableHeaderCell>
                  <TableHeaderCell>Latest outcome</TableHeaderCell>
                  <TableHeaderCell align="right">Actions</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data.properties || []).map((property) => {
                  const selected = selectedIds.has(property.id)
                  const wasSelected = initialIds.has(property.id)
                  const schedule = scheduleByProperty.get(property.id)
                  const latest = latestByProperty.get(property.id)
                  const cannotAdd = !wasSelected && (!data.eligible || !property.connectionReady)
                  const disabled = !data.canManage || saving || Boolean(retryingId) || cannotAdd
                  const propertyLabel = property.property_name || property.property_id
                  return (
                    <TableRow key={property.id}>
                      <TableCell>
                        <Checkbox
                          label={`Include ${propertyLabel}`}
                          checked={selected}
                          disabled={disabled}
                          onChange={() => toggleProperty(property.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <strong>{propertyLabel}</strong>
                        <span className="ga4-overnight-dialog__secondary">
                          {property.property_id}
                        </span>
                      </TableCell>
                      <TableCell>
                        {property.google_account_email || "No linked Google user"}
                        <span className="ga4-overnight-dialog__secondary">
                          {property.connectionReady ? "Refresh token ready" : "Reconnect required"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge variant={schedule?.lifecycle_state === "active" ? "accent" : "subtle"}>
                          {selected ? statusLabel(schedule?.lifecycle_state || "pending save") : "not selected"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusVariant(latest?.status)}>
                          {statusLabel(latest?.status)}
                        </Badge>
                        <span className="ga4-overnight-dialog__secondary">
                          {formatTimestamp(latest?.finished_at || latest?.started_at)}
                        </span>
                        {latest?.error_message ? (
                          <span className="ga4-overnight-dialog__error-detail" title={latest.error_message}>
                            {latest.error_message}
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell align="right">
                        {data.canManage && latest && (latest.status === "failed" || latest.status === "partial") ? (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={Boolean(retryingId) || saving}
                            onClick={() => retryItem(latest)}
                          >
                            {retryingId === latest.id ? "Retrying…" : "Retry"}
                          </Button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <div className="ga4-overnight-dialog__history">
            <h4>Recent run history</h4>
            {(data.history || []).length ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>Date</TableHeaderCell>
                      <TableHeaderCell>Property</TableHeaderCell>
                      <TableHeaderCell>Outcome</TableHeaderCell>
                      <TableHeaderCell>Attempts</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(data.history || []).slice(0, 50).map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{getRun(item)?.scheduled_for || formatTimestamp(item.created_at)}</TableCell>
                        <TableCell>{item.property_name_snapshot || item.property_id_snapshot}</TableCell>
                        <TableCell><Badge variant={statusVariant(item.status)}>{statusLabel(item.status)}</Badge></TableCell>
                        <TableCell>{item.attempt_count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : <p>No overnight runs in the last 30 days.</p>}
          </div>

          <div className="dialog__footer">
            {data.canManage && failedLatestItems.length ? (
              <Button
                variant="outline"
                disabled={Boolean(retryingId) || saving}
                onClick={retryAll}
              >
                {retryingId ? "Retrying failed audits…" : "Retry failed audits"}
              </Button>
            ) : null}
            <Button variant="secondary" disabled={saving || Boolean(retryingId)} onClick={onClose}>
              Close
            </Button>
            {data.canManage ? (
              <Button disabled={!hasChanges || saving || Boolean(retryingId)} onClick={saveSchedule}>
                {saving ? "Saving…" : "Save overnight list"}
              </Button>
            ) : null}
          </div>
        </>
      ) : null}
    </Dialog>
  )
}
